import {
  AccessParameterFrequency,
  AccessParameterKind,
  BeneficiaryPowerOfAppointmentPower,
  EntityKind,
  ScheduledDistributionFrequency,
  ScheduledDistributionKind,
} from '@/types/schema';

import { AccessParameterRadioGroupKind } from './EntityBeneficiariesSubform.types';

export const BENEFICIARIES_SUBFORM = 'beneficiariesSubform' as const;

/**
 * Sentinel value used in beneficiary forms to represent the "Surviving Spouse" option.
 * When this value is selected, the beneficiary is dynamically resolved to the surviving
 * spouse in the household at runtime, rather than linking to a specific individual.
 *
 * IMPORTANT: This option should ONLY be shown in subtrust (testamentary entity) beneficiary
 * forms for two-grantor households. It is NOT applicable to regular entity beneficiaries.
 */
export const SURVIVING_SPOUSE_SENTINEL = 'surviving-spouse' as const;
export const LIFETIME_BENEFICIARIES_SUBFORM =
  'lifetimeBeneficiariesSubform' as const;
export const REMAINDER_BENEFICIARIES_SUBFORM =
  'remainderBeneficiariesSubform' as const;
export const LEAD_BENEFICIARY_SUBFORM = 'leadBeneficiarySubform' as const;
export const INCOME_BENEFICIARIES_SUBFORM =
  'incomeBeneficiariesSubform' as const;
export const BENEFICIARIES_AFTER_BOTH_GRANTORS_DIE =
  'beneficiariesAfterBothGrantorsDie' as const;

export const ENTITY_BENEFICIARIES_FORM_VARIANTS = [
  'percentOnly',
  'default',
  'leadBeneficiary',
  'incomeBeneficiaries',
  'personalAccount',
  'nonTrustCharitableEntity',
  'testamentaryEntity',
] as const;

export const ACCESS_PARAMETER_KINDS: {
  display: string;
  value: AccessParameterKind;
}[] = [
  {
    display: '%',
    value: AccessParameterKind.Percentage,
  },
  {
    display: '$',
    value: AccessParameterKind.Amount,
  },
];

export const ACCESS_PARAMETER_RADIO_GROUP_KINDS: {
  label: string;
  value: AccessParameterRadioGroupKind;
}[] = [
  {
    label: 'Full access',
    value: AccessParameterRadioGroupKind.Full,
  },
  {
    label: 'Limited access',
    value: AccessParameterRadioGroupKind.Partial,
  },
];
export const UNITRUST_TYPE_ITEMS_V2: {
  label: string;
  summaryLabel: string;
  value: AccessParameterKind;
}[] = [
  {
    label: 'Standard',
    summaryLabel: 'standard',
    value: AccessParameterKind.Percentage,
  },
  {
    label: 'Net income (NI)',
    summaryLabel: 'net income',
    value: AccessParameterKind.NetIncome,
  },
  {
    label: 'Net income with makeup (NIM)',
    summaryLabel: 'net income with makeup',
    value: AccessParameterKind.NetIncomeWithMakeup,
  },
];

export const SCHEDULED_DISTRIBUTION_KINDS: {
  display: string;
  value: ScheduledDistributionKind;
}[] = [
  {
    display: '%',
    value: ScheduledDistributionKind.Percentage,
  },
  {
    display: '$',
    value: ScheduledDistributionKind.Amount,
  },
  {
    display: 'Trust income',
    value: ScheduledDistributionKind.AllIncome,
  },
];

export const SCHEDULED_DISTRIBUTION_FREQUENCY_ITEMS: {
  display: string;
  value: ScheduledDistributionFrequency;
}[] = [
  {
    display: 'One time',
    value: ScheduledDistributionFrequency.OneTime,
  },
  {
    display: 'Annually',
    value: ScheduledDistributionFrequency.Annually,
  },
  {
    display: 'Semiannually',
    value: ScheduledDistributionFrequency.Semiannually,
  },
  {
    display: 'Quarterly',
    value: ScheduledDistributionFrequency.Quarterly,
  },
  {
    display: 'Monthly',
    value: ScheduledDistributionFrequency.Monthly,
  },
];

export const POWER_OF_APPOINTMENT_ITEMS: {
  display: string;
  value: BeneficiaryPowerOfAppointmentPower;
}[] = [
  {
    display: 'General',
    value: BeneficiaryPowerOfAppointmentPower.General,
  },
  {
    display: 'Limited',
    value: BeneficiaryPowerOfAppointmentPower.Special,
  },
  {
    display: 'Other',
    value: BeneficiaryPowerOfAppointmentPower.Other,
  },
];

export const ACCESS_PARAMETER_FREQUENCY_ITEMS: {
  display: string;
  value: AccessParameterFrequency;
}[] = [
  {
    display: 'Annually',
    value: AccessParameterFrequency.Annually,
  },
  {
    display: 'Semiannually',
    value: AccessParameterFrequency.Semiannually,
  },
  {
    display: 'Quarterly',
    value: AccessParameterFrequency.Quarterly,
  },
  {
    display: 'Monthly',
    value: AccessParameterFrequency.Monthly,
  },
];

export const validUnitrustTypesV2 = UNITRUST_TYPE_ITEMS_V2.map(
  (item) => item.value
);

export const CURRENT_BENEFICIARY_ENTITY_KINDS: EntityKind[] = [
  EntityKind.IndividualPersonalAccount,
  EntityKind.RetirementPersonalAccount,
  EntityKind.JointPersonalAccount,
  EntityKind.IrrevocableTrust,
  EntityKind.RevocableTrust,
  EntityKind.SlatTrust,
  EntityKind.IlitTrust,
];

export const REMAINDER_BENEFICIARY_ENTITY_KINDS: EntityKind[] = [
  EntityKind.GratTrust,
  EntityKind.QprtTrust,
];

export function isCurrentBeneficiaryEntityKind(
  entityKind: EntityKind | null | undefined
): boolean {
  if (!entityKind) return false;

  return CURRENT_BENEFICIARY_ENTITY_KINDS.includes(entityKind);
}
